import type { Universe, PricingToggle } from '@/types/universe';
import type { AddRevealRef } from '@/hooks/useScrollReveal';
import { discountedPrice } from '@/lib/pricing';

interface PricingProps {
  universe: Universe;
  pricingToggles: PricingToggle[];
  activePricing: number;
  onPricingChange: (idx: number) => void;
  addRevealRef: AddRevealRef;
}

export default function Pricing({
  universe,
  pricingToggles,
  activePricing,
  onPricingChange,
  addRevealRef,
}: PricingProps) {
  return (
    <section id="pricing" className="bg-bg-dark py-24 px-6 md:px-16 lg:px-24">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <p className="flex items-center justify-center text-accent text-[11px] font-semibold uppercase tracking-widest mb-4">
            <span className="accent-line" />
            Tarifs
          </p>
          <h2
            ref={addRevealRef}
            className="reveal font-display text-white"
            style={{ fontSize: 'clamp(36px, 5vw, 72px)', fontFamily: "'DM Serif Display', Georgia, serif" }}>
            {universe.pricingTitle}{' '}
            <em className="italic text-accent">{universe.pricingTitleAccent}</em>
          </h2>
          <p className="text-white/60 mt-3">{universe.pricingDesc}</p>
          {/* Toggle */}
          <div className="inline-flex items-center gap-1 bg-white/5 border border-white/10 rounded-full px-1 py-1 mt-6">
            {pricingToggles.map((toggle, i) =>
              <button
                key={i}
                onClick={() => onPricingChange(i)}
                className={`flex items-center gap-2 px-5 py-2 rounded-full text-[11px] font-semibold uppercase tracking-widest transition-all duration-200 ${
                  i === activePricing ?
                  'bg-white text-primary' : 'text-white/60 hover:text-white'
                }`}>
                {toggle.label}
                {toggle.discount &&
                  <span className="bg-accent text-primary text-[10px] font-bold px-2 py-0.5 rounded-full">
                    {toggle.discount}
                  </span>
                }
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {universe.plans.map((plan, i) =>
            <div
              key={i}
              className={`pricing-card relative bg-white rounded-3xl p-8 ${
                plan.popular ? 'ring-2 ring-accent' : ''
              }`}>
              {plan.popular &&
  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
    <span className="whitespace-nowrap bg-accent text-primary text-[10px] font-bold uppercase tracking-widest px-4 py-1.5 rounded-full">
      Le plus populaire
    </span>
  </div>
}
              <div className="mb-6">
                <p className="text-muted text-[10px] font-semibold uppercase tracking-widest mb-1">{plan.tag}</p>
                <h3 className="text-primary font-bold text-2xl" style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}>
                  {plan.name}
                </h3>
              </div>
              <div className="flex items-baseline gap-1 mb-4">
                <span className="text-primary font-bold text-4xl" style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}>
                  {activePricing === 1 ? discountedPrice(plan.price) : plan.price}
                </span>
                <span className="text-muted text-sm">{plan.period}</span>
              </div>
              {activePricing === 1 &&
                <p className="text-muted text-xs line-through mb-2">{plan.price}</p>
              }
              <p className="text-muted text-sm leading-relaxed mb-6">{plan.desc}</p>
              <ul className="space-y-3 mb-8">
                {plan.features.map((feat, j) =>
                  <li key={j} className="flex items-start gap-3">
                    <span className={`flex-shrink-0 font-bold text-sm ${feat.included ? 'text-accent' : 'text-primary/20'}`}>
                      {feat.included ? '✓' : '✕'}
                    </span>
                    <span className={`text-sm ${feat.included ? 'text-primary/80' : 'text-primary/30'}`}>
                      {feat.text}
                    </span>
                  </li>
                )}
              </ul>
              <a
                href="#"
                className={`block text-center font-semibold text-[11px] uppercase tracking-widest px-6 py-4 rounded-full transition-all duration-200 ${
                  plan.popular ?
                  'bg-accent text-primary hover:bg-accent-light' : 'bg-primary text-white hover:bg-primary/90'
                }`}>
                {plan.cta}
              </a>
            </div>
          )}
        </div>
        <p className="text-center text-white/40 text-xs mt-8">· Paiement sécurisé</p>
      </div>
    </section>
  );
}
